import { gql, TypedDocumentNode, useMutation } from '@apollo/client';
import { Nullable } from '@e2m-customer-portal/shared';
import {
  Account,
  AssignedPermissionInput,
  CreateAccountMutation,
  CreateAccountMutationVariables,
} from '../../generated/graphql';
import { accountFragment } from './fragments';
import { accountsQuery } from './use-accounts';

export const createAccountMutation: TypedDocumentNode<CreateAccountMutation, CreateAccountMutationVariables> = gql`
  mutation CreateAccount(
    $email: String!
    $name: String!
    $permissions: [AssignedPermissionInput!]!
    $marketLocationIds: [ID!]
  ) {
    createAccount(email: $email, name: $name, permissions: $permissions, marketLocationIds: $marketLocationIds) {
      ...AccountFragment
    }
  }
  ${accountFragment}
`;

type CreateAccount = (
  email: string,
  name: string,
  permissions: readonly AssignedPermissionInput[],
  marketLocationIds?: Nullable<string[]>,
) => Promise<Account>;

export function useCreateAccount(): CreateAccount {
  const [update] = useMutation(createAccountMutation);

  return async (email, name, permissions, marketLocationIds) => {
    const { data, errors } = await update({
      variables: {
        name,
        email,
        permissions: permissions.map((permission) => ({
          permissionId: permission.permissionId,
          granted: permission.granted,
        })),
        marketLocationIds,
      },
      update(cache, response) {
        // Update list of accounts to contain the new account
        const existingData = cache.readQuery({ query: accountsQuery });
        if (existingData && response.data) {
          cache.writeQuery({
            query: accountsQuery,
            // @ts-ignore "TS2589: Type instantiation is excessively deep and possibly infinite."
            data: {
              accounts: [...existingData.accounts, response.data.createAccount],
            },
          });
        }
      },
    });

    if (errors?.length) {
      throw errors[0];
    }

    if (!data) {
      // Should not happen: https://github.com/apollographql/apollo-client/issues/6374
      throw new Error('Expected data, nothing received.');
    }

    return data.createAccount;
  };
}
